import React, { useState, useEffect } from 'react';
import { Mail, GraduationCap, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';
import { getImageUrl, getFileUrl } from '../lib/utils';

const Faculty = () => {
  const [faculty, setFaculty] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/faculty/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([facultyData, settingsData]) => {
        setFaculty(facultyData);
        setSettings(settingsData);
      })
      .catch(error => console.error('Error fetching data:', error))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <PageHero 
        title="أعضاء هيئة التدريس"
        subtitle="نخبة متميزة من أعضاء هيئة التدريس بمؤهلات عالمية"
        backgroundImage={getImageUrl(settings?.page_faculty_bg)}
      />

      {/* Faculty Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {faculty.length === 0 ? (
            <div className="text-center py-20">
              <GraduationCap className="w-20 h-20 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-xl">لا يوجد أعضاء هيئة تدريس حالياً</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {faculty.map((member) => (
                <Card key={member.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <div className="relative h-80 overflow-hidden">
                    {member.image ? (
                      <img
                        src={getImageUrl(member.image)}
                        alt={member.name_ar}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] flex items-center justify-center">
                        <GraduationCap className="w-24 h-24 text-white/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 right-0 left-0 p-6 text-white">
                      <h3 className="text-xl font-bold mb-1">{member.name_ar}</h3>
                      {member.title_ar && (
                        <p className="text-[#C9A040] text-sm font-semibold">{member.title_ar}</p>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    {member.position_ar && (
                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-1">المنصب</div>
                        <div className="text-sm font-medium text-gray-700">{member.position_ar}</div>
                      </div>
                    )}
                    
                    {(member.degree_ar || member.academic_qualification_ar) && (
                      <div className="flex items-start gap-3 mb-4">
                        <GraduationCap className="w-5 h-5 text-[#C9A040] flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs text-gray-500 mb-1">المؤهلات</div>
                          <div className="text-sm text-gray-700">
                            {member.degree_ar && <div>{member.degree_ar}</div>}
                            {member.academic_qualification_ar && <div className="text-xs">{member.academic_qualification_ar}</div>}
                          </div>
                        </div>
                      </div>
                    )}

                    {member.specialization_ar && (
                      <div className="mb-4 pb-4 border-b">
                        <div className="text-xs text-gray-500 mb-1">التخصص</div>
                        <div className="text-sm text-gray-700">{member.specialization_ar}</div>
                      </div>
                    )}

                    <div className="space-y-2">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="flex items-center gap-2 text-[#0F388E] hover:text-[#C9A040] transition-colors text-sm"
                        >
                          <Mail className="w-4 h-4" />
                          التواصل
                        </a>
                      )}
                      {member.cv_pdf && (
                        <a
                          href={getFileUrl(member.cv_pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[#0F388E] hover:text-[#C9A040] transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          تحميل السيرة الذاتية
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F388E] mb-4">هيئة التدريس بالأرقام</h2>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '50+', label: 'عضو هيئة تدريس' },
              { number: '30+', label: 'أستاذ دكتور' },
              { number: '15+', label: 'أستاذ مشارك' },
              { number: '10+', label: 'جنسيات مختلفة' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#0F388E] mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faculty;